import { Box, Stack, Typography } from "@mui/material";
import { UnstyledButton } from "Components/Shared/Buttons/UnstyledButton";
import { CloseIcon } from "Components/Shared/Icons";
import { StyledInputLabel } from "Components/Shared/StyledComponents";
import { VISUALLY_HIDDEN_STYLE } from "Components/Shared/visuallyHiddenStyle";
import { SUPPORT_ATTACHMENT_ACCEPTED_TYPES } from "Components/Support/supportRequestSchema";
import { useId, useRef } from "react";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Support.Request;

type Props<FormType extends FieldValues, TransformedFieldValues = FormType> = {
  name: FieldPath<FormType>;
  control: Control<FormType, unknown, TransformedFieldValues>;
  label: string;
};

// A single-line bordered picker, matching the design. The shared DragNDrop is a
// dashed dropzone with its own icon, which is a different control altogether.
export const SupportAttachmentInput = <T extends object, V extends object>({
  name,
  control,
  label,
}: Props<T, V>) => {
  const { t } = useResource();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const file: File | null = value;

        const handleClear = () => {
          onChange(null);

          if (inputRef.current) {
            inputRef.current.value = "";
          }
        };

        return (
          <Box>
            <StyledInputLabel htmlFor={inputId} hasError={!!error}>
              {label}
            </StyledInputLabel>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              px={3}
              py={2}
              borderRadius="4px"
              border={1}
              borderColor={error ? "error.main" : "divider"}
              sx={({ palette }) => ({
                // The input is visually hidden, so the frame has to show its focus ring —
                // otherwise a keyboard user can reach the picker but cannot see where they are.
                // Inset, because a step-transition wrapper clips to its measured height and
                // would cut an outer glow off along the left and right edges.
                "&:focus-within": {
                  boxShadow: `inset 0 0 0 2px ${palette.primary.main}`,
                },
              })}
            >
              <Box
                component="label"
                htmlFor={inputId}
                minWidth={0}
                flexGrow={1}
                sx={{ cursor: "pointer" }}
              >
                <Typography noWrap color={file ? "text.primary" : "inherit"}>
                  {file ? file.name : t(RESOURCES.AttachmentPlaceholder)}
                </Typography>
              </Box>
              {!!file && (
                <UnstyledButton
                  onClick={handleClear}
                  aria-label={t(RESOURCES.AttachmentRemove)}
                  sx={{ cursor: "pointer", lineHeight: 0 }}
                >
                  <CloseIcon width={14} height={14} />
                </UnstyledButton>
              )}
              <input
                id={inputId}
                ref={inputRef}
                type="file"
                style={VISUALLY_HIDDEN_STYLE}
                accept={SUPPORT_ATTACHMENT_ACCEPTED_TYPES.join(",")}
                onChange={(event) => onChange(event.target.files?.[0] ?? null)}
              />
            </Stack>
            {!!error && (
              <Typography variant="body2" color="error" mt={1}>
                {error.message}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
};
